import { CircularProgress } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminTradeData, updateSortModel as updateAdminSortModel } from "../slices/adminTrade/adminTradeSlice.js";
import {
  selectEmployeeTradeData,
  updateSortModel as updateEmployeeSortModel
} from "../slices/employeeTrade/employeeTradeSlice.js";
import { generateExportCsvButtonText } from "../util/commonUtils.js";
import { ACQUIRED_SHIFT, LAST_UPDATED, STATUS, TRADE_PAGE_TYPE, TRADE_TYPE } from "../util/constants.js";
import { getFormattedTimeStamp } from "../util/dateTimeFormatter.js";
import {
  getAdminTradeStatusCustomComparator,
  getLastUpdatedCustomComparator,
  getTradeOfferedEmployeeLastNameCustomComparator,
  getTradeOfferedEmployeeShiftCustomComparator,
  getTradeStatusCustomComparator
} from "../util/sortTrades.js";
import {
  parseOfferedEmployeeId,
  parseOfferedEmployeeName,
  parseOfferedReliefShift,
  parseOfferedShift,
  parseOfferedWorkgroup,
  parseRequestedEmployeeId,
  parseRequestedEmployeeName,
  parseRequestedReliefShift,
  parseRequestedShift,
  parseRequestedWorkgroup,
  parseShiftWorkgroup,
  parseStatusValue,
  parseTradeBetweenAgentAndSup
} from "../util/tradeFormExportMapper";
import AdminTradeFormToolbar from "./AdminTradeFormToolbar";
import CustomErrorTableOverlay from "./CustomErrorTableOverlay.jsx";
import CustomLoadingTableOverlay from "./CustomLoadingTableOverlay.jsx";
import CustomNoRowsTableOverlay from "./CustomNoRowsTableOverlay.jsx";
import EmployeeCell from "./EmployeeCell.jsx";
import EmployeeShiftCell from "./EmployeeShiftCell.jsx";
import EmployeeTradeMobile from "./EmployeeTradeMobile.jsx";
import ErrorMessage from "./ErrorMessage";
import NoTradesImage from "./NoTradesImage.jsx";
import StatusCell from "./StatusCell.jsx";
import TradeFormAction from "./TradeFormAction.jsx";
import TradeFormType from "./TradeFormType.jsx";

const EmployeeTradeTable = (props) => {
  const [selectionModel, setSelectionModel] = React.useState([]);
  const adminTradeData = useSelector(selectAdminTradeData);
  const employeeTradeData = useSelector(selectEmployeeTradeData);
  const dispatch = useDispatch();

  const renderTradeFormType = (trade) => {
    return <TradeFormType trade={trade} isAdminPage={props.isAdminPage} loggedInUserData={props.loggedInUserData} isDesktopView={props.isDesktopView} />;
  };

  const getStatusField = () => {
    return props.isAdminPage ? "visibleTradeStatusAdmin" : "visibleTradeStatus";
  };

  const renderStatuses = (trade) => {
    return <StatusCell status={trade[getStatusField()]} isAdminPage={props.isAdminPage} />;
  };

  const renderEmployees = (trade) => {
    let empOneData = {
      empNumber: "1",
      employeeLastName: trade.offeredEmployeeLastName,
      employeeFirstName: trade.offeredEmployeeFirstName,
      employeeId: trade.offeredEmployeeId,
      employeeWorkgroup: trade.offeredEmployeeWorkgroup,
      shiftStart: trade.offeredShiftStart,
      shiftEnd: trade.offeredShiftEnd,
    };
    let empTwoData = {
      empNumber: "2",
      employeeLastName: trade.requestedEmployeeLastName,
      employeeFirstName: trade.requestedEmployeeFirstName,
      employeeId: trade.requestedEmployeeId,
      employeeWorkgroup: trade.requestedEmployeeWorkgroup,
      shiftStart: trade.requestedShiftStart,
      shiftEnd: trade.requestedShiftEnd,
    };
    return <EmployeeCell empOne={empOneData} empTwo={empTwoData} />;
  };

  const renderEmployeeShifts = (trade) => {
    let empOneData = {
      empNumber: "1",
      employeeLastName: trade.offeredEmployeeLastName,
      employeeFirstName: trade.offeredEmployeeFirstName,
      employeeId: trade.offeredEmployeeId,
      employeeWorkgroup: trade.offeredEmployeeWorkgroup,
      shiftStart: trade.offeredShiftStart,
      shiftEnd: trade.offeredShiftEnd,
    };
    let empTwoData = {
      empNumber: "2",
      employeeLastName: trade.requestedEmployeeLastName,
      employeeFirstName: trade.requestedEmployeeFirstName,
      employeeId: trade.requestedEmployeeId,
      employeeWorkgroup: trade.requestedEmployeeWorkgroup,
      shiftStart: trade.requestedShiftStart,
      shiftEnd: trade.requestedShiftEnd,
    };
    return <EmployeeShiftCell empOneShift={empOneData} empTwoShift={empTwoData} />;
  };

  const renderAction = (trade) => {
    return <TradeFormAction loggedInUserData={props.loggedInUserData} trade={trade} isAdminPage={props.isAdminPage} />;
  };

  const renderDesktopTrades = () => {
    const columns = [
      {
        field: "createdAt",
        headerName: LAST_UPDATED,
        type: "dateTime",
        valueFormatter: (params) => {
          return getFormattedTimeStamp(params.value);
        },
        sortComparator: getLastUpdatedCustomComparator,
        flex: 0.2,
      },
      {
        field: "tradeFormType",
        renderCell: (params) => {
          return renderTradeFormType(params.row);
        },
        headerName: TRADE_TYPE,
        type: "string",
        flex: 0.14,
      },
      {
        field: getStatusField(),
        valueGetter: (params) => {
          return params.value;
        },
        valueFormatter: ({ value }) => {
          return value;
        },
        renderCell: (params) => {
          return renderStatuses(params.row);
        },
        headerName: STATUS,
        type: "string",
        flex: 0.2,
        sortComparator: props.isAdminPage
          ? getAdminTradeStatusCustomComparator
          : (v1, v2, param1, param2) => {
              return getTradeStatusCustomComparator(v1, v2, param1, param2, props.loggedInUserData.eid);
            },
      },
      { field: "workgroup", headerName: "Workgroup", type: "string", flex: 0.16 },
      {
        field: "employees",
        valueGetter: (params) => {
          return params.value;
        },
        renderCell: (params) => {
          return renderEmployees(params.row);
        },
        headerName: "Employees",
        type: "string",
        flex: 0.4,
        sortComparator: getTradeOfferedEmployeeLastNameCustomComparator,
      },
      {
        field: "shift",
        valueGetter: (params) => {
          return params.value;
        },
        renderCell: (params) => {
          return renderEmployeeShifts(params.row);
        },
        headerName: ACQUIRED_SHIFT,
        type: "string",
        flex: 0.25,
        sortComparator: getTradeOfferedEmployeeShiftCustomComparator,
      },
      {
        field: "action",
        renderCell: (params) => {
          return renderAction(params.row);
        },
        headerName: "Actions",
        type: "string",
        flex: 0.15,
        sortable: false,
      },
      {
        field: "employee1Name",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => parseOfferedEmployeeName(value),
        headerName: "EE #1 Name",
        type: "string",
        hide: true,
      },
      {
        field: "employee2Name",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => parseRequestedEmployeeName(value),
        headerName: "EE #2 Name",
        type: "string",
        hide: true,
      },
      {
        field: "employee1AcquiredShift",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => parseRequestedShift(value),
        headerName: `EE #1 ${ACQUIRED_SHIFT}`,
        type: "string",
        hide: true,
      },
      {
        field: "employee2AcquiredShift",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => parseOfferedShift(value),
        headerName: `EE #2 ${ACQUIRED_SHIFT}`,
        type: "string",
        hide: true,
      },
      {
        field: "station",
        valueGetter: () => {
          return props?.station;
        },
        type: "string",
        headerName: "Station",
        hide: true,
      },
      {
        field: "referenceNumber",
        headerName: "Reference #",
        type: "string",
        hide: true,
      },
      {
        field: "employee1Id",
        headerName: "EE #1 ID",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => parseOfferedEmployeeId(value),
        type: "string",
        hide: true,
      },
      {
        field: "employee2Id",
        headerName: "EE #2 ID",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => parseRequestedEmployeeId(value),
        type: "string",
        hide: true,
      },
      {
        field: "employee1Workgroup",
        headerName: "EE #1 Workgroup",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => parseOfferedWorkgroup(value),
        type: "string",
        hide: true,
      },
      {
        field: "employee2Workgroup",
        headerName: "EE #2 Workgroup",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => parseRequestedWorkgroup(value),
        type: "string",
        hide: true,
      },
      {
        field: "employee2ReliefShift",
        headerName: "EE #2 Relief Shift",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => parseOfferedReliefShift(value),
        type: "string",
        hide: true,
      },
      {
        field: "employee1ReliefShift",
        headerName: "EE #1 Relief Shift",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => parseRequestedReliefShift(value),
        type: "string",
        hide: true,
      },
      {
        field: "supervisorInvolved",
        headerName: "Trade Between Agent & Sup",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => parseTradeBetweenAgentAndSup(value),
        type: "string",
        hide: true,
      },
      {
        field: "visibleTradeStatusAdminCSV",
        headerName: "Status",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => parseStatusValue(value),
        type: "string",
        hide: true,
      },
      {
        field: "shiftWorkgroup",
        headerName: "Shift Workgroup",
        valueGetter: (params) => {
          return params.row;
        },
        valueFormatter: ({ value }) => parseShiftWorkgroup(value),
        type: "string",
        hide: true,
      },
    ];

    return (
      <>
        <div style={{ width: "100%", height: "inherit" }}>
          <DataGridPro
            apiRef={props.tradeTableDesktopRef}
            rows={props.employeeTrades}
            columns={columns}
            sortModel={props.isAdminPage ? adminTradeData.sortModel : employeeTradeData.sortModel}
            onSortModelChange={(event) => {
              dispatch(props.isAdminPage ? updateAdminSortModel(event) : updateEmployeeSortModel(event));
            }}
            sortingOrder={["asc", "desc"]}
            disableColumnMenu={true}
            checkboxSelection={props.isAdminPage ? true : false}
            onSelectionModelChange={(newSelectionModel) => {
              setSelectionModel(newSelectionModel);
            }}
            selectionModel={selectionModel}
            onCellClick={(params, event) => {
              event.defaultMuiPrevented = true;
              setSelectionModel([params.id]);
            }}
            onRowClick={(params, event) => {
              event.defaultMuiPrevented = true;
            }}
            hideFooterPagination={true}
            rowHeight={100}
            components={{
              LoadingOverlay: CustomLoadingTableOverlay,
              NoRowsOverlay: CustomNoRowsTableOverlay,
              ErrorOverlay: CustomErrorTableOverlay,
              Toolbar: props.isAdminPage ? AdminTradeFormToolbar : null,
            }}
            componentsProps={{
              noRowsOverlay: { pageType: TRADE_PAGE_TYPE },
              errorOverlay: {
                errorMessage: props.errorObject.message,
                errorCode: props.errorObject.code,
              },
              toolbar: { station: props.station },
            }}
            loading={props.isLoading}
            error={props.isError}
            localeText={{
              toolbarExport: "Export",
              toolbarExportCSV: generateExportCsvButtonText(selectionModel, props.employeeTrades),
            }}
          />
        </div>
      </>
    );
  };

  const renderMobileTrades = () => {
    return (
      <>
        <div ref={props.tradeTableMobileRef} className="enable-scroll">
          {props.isError ? (
            <ErrorMessage errorMessage={props.errorObject.message} errorCode={props.errorObject.code} />
          ) : props.isLoading ? (
            <CircularProgress />
          ) : props.employeeTrades.length === 0 ? (
            <NoTradesImage />
          ) : (
            props.employeeTrades.map((trade) => (
              <EmployeeTradeMobile
                key={trade.eventId}
                loggedInUserData={props.loggedInUserData}
                isAdminPage={props.isAdminPage}
                trade={trade}
                isDesktopView={props.isDesktopView}
              />
            ))
          )}
        </div>
      </>
    );
  };

  return <div className="stars-table">{props.isDesktopView ? renderDesktopTrades() : renderMobileTrades()}</div>;
};

EmployeeTradeTable.propTypes = {
  loggedInUserData: PropTypes.object.isRequired,
  employeeTrades: PropTypes.array.isRequired,
  station: PropTypes.string,
  isAdminPage: PropTypes.bool.isRequired,
  isFirstLoad: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
  errorObject: PropTypes.object.isRequired,
  tradeTableDesktopRef: PropTypes.object.isRequired,
  tradeTableMobileRef: PropTypes.object,
  isDesktopView: PropTypes.bool.isRequired,
};

export default EmployeeTradeTable;
